/*
 * The drag-to-stop mapping of `ScrubButton` (pure, so it is tested on its own). One axis rule
 * everywhere: right and up are "more", left and down are "less", added together (a drag up and to
 * the right is the quickest way up; up and to the left cancel out).
 */

/** How far a press may wander (px) before it is a drag; a press that stays inside is a tap. */
export const SCRUB_DEAD_ZONE_PX = 10

/** Drag distance (px, along the "more" direction) from one stop to the next. */
export const SCRUB_STEP_PX = 28

/** True once a press at (dx, dy) from where it went down has left the dead zone. */
export function scrubPassedDeadZone(dx: number, dy: number, deadZone = SCRUB_DEAD_ZONE_PX): boolean {
  return dx * dx + dy * dy > deadZone * deadZone
}

/** How far a drag has gone toward "more" (px): right counts up, and so does up (screen y down). */
export function scrubOffset(dx: number, dy: number): number {
  return dx - dy
}

/** Where a drag is measured from: the stop it was at, at this offset. */
export type ScrubAnchor = { stop: number; offset: number }

/**
 * The stop a drag reaches at `offset` from `anchor` over `stops` stops, and the anchor to carry on
 * from. The stop changes half a step either side of each stop. Past either end the anchor moves
 * with the finger, so the way back responds at once instead of first unwinding the overshoot.
 */
export function scrubStop(anchor: ScrubAnchor, offset: number, stops: number, stepPx = SCRUB_STEP_PX): { stop: number; anchor: ScrubAnchor } {
  const last = Math.max(0, Math.floor(stops) - 1)
  const raw = anchor.stop + (offset - anchor.offset) / stepPx
  if (raw > last) return { stop: last, anchor: { stop: last, offset } }
  if (raw < 0) return { stop: 0, anchor: { stop: 0, offset } }
  return { stop: Math.min(last, Math.max(0, Math.round(raw))), anchor }
}

/** One key step from `stop` (by -1 or +1), clamped to the stops. */
export function scrubKeyStep(stop: number, by: number, stops: number): number {
  return Math.max(0, Math.min(Math.max(0, Math.floor(stops) - 1), Math.round(stop) + by))
}
